import React, { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { showSuccessMessage } from '../../../../utils/toast';
import { useQuery, useMutation } from '@tanstack/react-query';
import api, { API_BASE_URL } from '../../../../services/api';
import toast from 'react-hot-toast';

const BANK_NAMES = [
  "HDFC Bank",
  "State Bank of India (SBI)",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Bank of Baroda",
  "Punjab National Bank",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank"
];

export const OrderDetails = ({ ordersList, initialOrderId, onPaymentComplete }) => {
  const safeOrdersList = Array.isArray(ordersList) ? ordersList.filter(o => o.status !== 'SAVED') : [];
  const [selectedOrderId, setSelectedOrderId] = useState(initialOrderId || (safeOrdersList.length > 0 ? safeOrdersList[0]._id : null));
  
  const currentOrder = safeOrdersList.find(o => o._id === selectedOrderId) || safeOrdersList[0] || {};
  const orderIdStr = currentOrder._id ? `SO-${currentOrder._id.substring(currentOrder._id.length - 6).toUpperCase()}` : 'SO-1039';
  const customerName = currentOrder.customer_id?.name || currentOrder.customer_id?.customerName || currentOrder.customerName || 'Customer';
  const status = currentOrder.status || 'SAVED';

  const items = (currentOrder.items && currentOrder.items.length > 0) ? currentOrder.items.map(item => {
    const qty = Number(item.qty) || 1;
    const rate = Number(item.item_details?.price || item.item_details?.purchaseRate || item.item_details?.rate || 0);
    const lineAmount = qty * rate;

    const productTitle = item.item_details?.product || item.item_details?.name || item.item_details?.title || 'Custom Item';
    const h = item.item_details?.height;
    const w = item.item_details?.width;
    const sizeText = w && h ? `(${w} x ${h})` : (w || h ? `(${w || h})` : (item.item_details?.dimensions ? `(${item.item_details.dimensions})` : ''));

    return {
      image: item.image_id ? `${API_BASE_URL}/api/estimations/images/${item.image_id}` : (item.item_details?.image ? (item.item_details.image.startsWith('http') ? item.item_details.image : (item.item_details.image.startsWith('/') ? `${API_BASE_URL}${item.item_details.image}` : `${API_BASE_URL}/api/inventory/settings/images/${item.item_details.image}`)) : ''),
      product: productTitle,
      sizeText: sizeText,
      thickness: item.item_details?.thickness || '—',
      quality: item.item_details?.quality || 'Premium',
      color: item.item_details?.color || '',
      profile: item.item_details?.profile || '',
      qty: qty,
      rate: rate,
      amount: lineAmount,
    };
  }) : [];

  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const discountType = currentOrder.discount_type || '%';
  const discountValue = currentOrder.discount_value || 0;
  let discountAmount = 0;
  // Handles both '%' (saved by estimation) and 'percentage' (legacy)
  if (discountType === '%' || discountType === 'percentage') {
    discountAmount = Math.round(subtotal * (discountValue / 100));
  } else {
    discountAmount = Number(discountValue);
  }
  const taxableValue = subtotal - discountAmount;
  const gstValue = currentOrder.gst_value || 0;
  const gstAmount = Math.round(taxableValue * (gstValue / 100));
  const grandTotal = taxableValue + gstAmount;

  const numberToWords = (num) => {
    if (!num) return 'Zero Rupees Only';
    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    if ((num = num.toString()).length > 9) return 'overflow';
    const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return '';
    let str = '';
    str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
    str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
    str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
    str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
    str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
    return str.trim() ? str.trim() + ' Rupees Only' : 'Zero Rupees Only';
  };

  const [showChangeLogModal, setShowChangeLogModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Payment Form States
  const [paymentMethod, setPaymentMethod] = useState('cheque');
  const [payAmount, setPayAmount] = useState('');

  // Cheque fields
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [issuingBank, setIssuingBank] = useState('');

  // UPI fields
  const [upiRefNo, setUpiRefNo] = useState('');
  const [upiPhone, setUpiPhone] = useState('');
  const [upiRemarks, setUpiRemarks] = useState('');

  // Cash fields
  const [cashReceiverName, setCashReceiverName] = useState('');
  const [cashReceiverPhone, setCashReceiverPhone] = useState('');
  const [cashInternalRemarks, setCashInternalRemarks] = useState('');

  // Bank Transfer fields
  const [bankRefId, setBankRefId] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [internalRemarks, setInternalRemarks] = useState('');

  const { data: logsData } = useQuery({
    queryKey: ['estimation-logs', currentOrder._id],
    queryFn: async () => {
      if (!currentOrder._id) return [];
      const res = await api.get(`/estimations/${currentOrder._id}/logs`);
      return res.data?.data || [];
    },
    enabled: !!currentOrder._id && showChangeLogModal
  });
  const logs = logsData || [];

  const discType = currentOrder.discount_type || '%';
  const discVal = Number(currentOrder.discount_value) || 0;
  const effectiveDiscountPercent = discType === '%' ? discVal : (subtotal > 0 ? (discVal / subtotal) * 100 : 0);

  const hasPaymentLog = logs.some(log => log.notes && (log.notes.includes('Payment of ₹') || log.action === 'Payment Recorded'));

  const STAGES = ['ORDER_NOT_PLACED', 'ORDER_PLACED', 'PENDING_APPROVAL', 'SENT_TO_STORE', 'COMPLETED', 'DISPATCHED'];
  const STAGES_DISPLAY = ['Order not placed', 'Order placed', 'Pending approval', 'Sent to store', 'Completed', 'Dispatched'];
  
  // Handle 'SAVED' mapping to 'ORDER_NOT_PLACED' and 'APPROVED' mapping to 'SENT_TO_STORE'
  const getStageName = (s) => {
    if (s === 'SAVED') return 'ORDER_NOT_PLACED';
    if (s === 'APPROVED') return 'SENT_TO_STORE';
    return s;
  };
  const currentStageIndex = STAGES.indexOf(getStageName(status));
  const activeIndex = currentStageIndex >= 0 ? currentStageIndex : 0;

  const recordPaymentMutation = useMutation({
    mutationFn: async () => {
      let methodDisplay = "Credit / Debit Card";
      let paymentData = {
        amount: Number(payAmount) || 0,
        paymentMethod: paymentMethod,
      };

      if (paymentMethod === 'cheque') {
        methodDisplay = "Cheque";
        paymentData.chequeNumber = chequeNumber;
        paymentData.chequeDate = chequeDate;
        paymentData.bankName = issuingBank;
      } else if (paymentMethod === 'upi') {
        methodDisplay = `UPI`;
        paymentData.upiRefNo = upiRefNo;
        paymentData.upiPhone = upiPhone;
        paymentData.remarks = upiRemarks;
      } else if (paymentMethod === 'cash') {
        methodDisplay = "Cash";
        paymentData.receiverName = cashReceiverName;
        paymentData.receiverPhone = cashReceiverPhone;
        paymentData.remarks = cashInternalRemarks;
      } else if (paymentMethod === 'bank') {
        methodDisplay = `Bank Transfer (${bankName})`;
        paymentData.transactionId = bankRefId;
        paymentData.bankName = bankName;
        paymentData.branch = bankBranch;
        paymentData.ifsc = bankIfsc;
        paymentData.remarks = internalRemarks;
      }
      
      // Save payment to the recieved amounts collection
      await api.post(`/accounts/recieved_amounts`, {
        estimation_id: currentOrder._id,
        customer_id: currentOrder.customer_id?._id || currentOrder.customer_id,
        date: new Date().toISOString(),
        amount: paymentData.amount,
        paymentMethod: paymentData.paymentMethod,
        ...paymentData
      });

      // Do NOT change status to PENDING_APPROVAL automatically upon payment, 
      // because PENDING_APPROVAL is for Estimation Approvals (Discount Limit).
      // The order is already in ORDER_PLACED, which means it will appear in Order Approvals.
      // We just need to add a log/note if we want.
      if (activeIndex <= 1) {
        await api.put(`/estimations/${currentOrder._id}/status`, { 
          status: 'ORDER_PLACED', 
          approval_notes: `Advance payment of ₹${Number(payAmount || grandTotal).toLocaleString('en-IN')} received via ${methodDisplay}`,
        });
      }
    },
    onSuccess: () => {
      setShowPaymentModal(false);
      showSuccessMessage(`Advance payment recorded successfully!`);
      if (onPaymentComplete) onPaymentComplete();
    }
  });

  const handleConfirmPayment = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    if (!currentOrder._id) {
      // Demo mode
      setShowPaymentModal(false);
      showSuccessMessage('Advance payment recorded successfully!');
      if (onPaymentComplete) onPaymentComplete();
      return;
    }
    recordPaymentMutation.mutate();
  };

  const requestActionMutation = useMutation({
    mutationFn: async (newStatus) => {
      const res = await api.put(`/estimations/${currentOrder._id}/status`, { 
        status: newStatus
      });
      return res.data;
    },
    onSuccess: (data, newStatus) => {
      showSuccessMessage(newStatus === 'PENDING_APPROVAL' ? 'Sent for approval successfully.' : 'Order sent to store successfully.');
      if (onPaymentComplete) onPaymentComplete();
    }
  });

  const handleRequestAction = () => {
    if (!currentOrder._id) return;
    requestActionMutation.mutate('PENDING_APPROVAL');
  };
    const progressPercentage = (activeIndex / (STAGES.length - 1)) * 100;

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:max-w-lg">
          <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">Select Order:</span>
          <select 
            className="w-full border border-slate-300 rounded-md text-sm px-3 py-1.5 focus:outline-none focus:border-black bg-slate-50 text-slate-700 font-medium cursor-pointer"
            value={selectedOrderId || ''}
            onChange={(e) => setSelectedOrderId(e.target.value)}
          >
            {safeOrdersList.length === 0 && <option value="" disabled>No Orders Found</option>}
            {safeOrdersList.map((est, idx) => {
              const estNum = safeOrdersList.length - idx;
              const dateStr = new Date(est.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <option key={est._id} value={est._id}>
                  Order #{estNum} — {dateStr} ({est.status.replace(/_/g, ' ')})
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-end mb-8">
        {/*
        {activeIndex <= 1 && hasPaymentLog && effectiveDiscountPercent > 25 && (
          <button 
            onClick={handleRequestAction}
            disabled={requestActionMutation.isPending}
            className="px-6 py-2.5 bg-[#C91D25] text-white rounded-lg text-sm font-semibold hover:bg-[#a8161d] shadow-sm transition-all flex items-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
          >
            {requestActionMutation.isPending ? 'Processing...' : 'Request Discount Approval'} <ArrowUpRight size={16} />
          </button>
        )}
        */}

        {activeIndex === 2 && (
          <button disabled className="px-6 py-2.5 bg-[#0f172a] text-white rounded-lg text-sm font-semibold opacity-50 cursor-not-allowed">
            Pending Approval
          </button>
        )}
        
        {activeIndex >= 3 && (
          <button disabled className="px-6 py-2.5 bg-[#0f172a] text-white rounded-lg text-sm font-semibold opacity-50 cursor-not-allowed flex items-center gap-2">
            Already Sent to Store <ArrowUpRight size={16} />
          </button>
        )}
      </div>

      {/* Progress Tracker */}
      <div className="flex items-center justify-between mb-10 relative px-4">
        {/* Background Connecting Line */}
        <div className="absolute top-[10px] left-8 right-8 h-[2px] bg-slate-200 z-0"></div>
        {/* Active Progress Line */}
        <div className="absolute top-[10px] left-8 h-[2px] bg-emerald-500 z-0 transition-all duration-500" style={{ width: `${progressPercentage}%`, maxWidth: 'calc(100% - 4rem)' }}></div>
        
        {STAGES_DISPLAY.map((stage, idx) => (
          <div key={idx} className="flex flex-col items-center gap-2 bg-white px-2 relative z-10">
            <div className={`w-5 h-5 rounded-full border-2 border-white ${idx <= activeIndex ? 'bg-emerald-500 ring-2 ring-emerald-500/20 shadow-xs' : 'bg-slate-200'}`}></div>
            <span className={`text-[12px] font-semibold ${idx <= activeIndex ? 'text-slate-700' : 'text-slate-400'}`}>{stage}</span>
          </div>
        ))}
      </div>

      {/* Items Table */}
      <div className="mb-6 rounded border border-slate-200 overflow-hidden">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500 text-[12px] uppercase tracking-wider">
              <th className="py-3 px-4 font-semibold text-center w-12">S.No</th>
              <th className="py-3 px-4 font-semibold text-center w-20">Image</th>
              <th className="py-3 px-4 font-semibold">Product</th>
              <th className="py-3 px-4 font-semibold">Thickness</th>
              <th className="py-3 px-4 font-semibold">Quality</th>
              <th className="py-3 px-4 font-semibold text-center">Quantity</th>
              <th className="py-3 px-4 font-semibold text-right">Unit Price</th>
              <th className="py-3 px-4 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {items.map((item, idx) => {
              const displayAmount = item.amount.toLocaleString('en-IN');
              const displayRate = item.rate > 0 ? item.rate.toLocaleString('en-IN') : '—';

              return (
                <tr key={idx}>
                  <td className="py-3 px-4 text-center text-slate-700">{idx + 1}</td>
                  <td className="py-3 px-4 text-center flex justify-center">
                    {item.image ? (
                      <img src={item.image} alt={item.product} className="w-10 h-10 object-contain rounded bg-slate-50" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-[10px] text-slate-400">N/A</div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-800 text-[13px]">
                    <div className="uppercase">{item.product}</div>
                    {item.sizeText && <div className="text-[12px] text-slate-500 mt-0.5">{item.sizeText}</div>}
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-[13px]">{item.thickness}</td>
                  <td className="py-3 px-4 text-slate-600 text-[13px]">{item.quality}</td>
                  <td className="py-3 px-4 text-center font-semibold text-slate-800">{item.qty}</td>
                  <td className="py-3 px-4 text-right text-slate-600">{displayRate}</td>
                  <td className="py-3 px-4 text-right text-slate-900">{displayAmount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Subtotal, GST & Grand Total Summary */}
      <div className="flex justify-between items-end mb-8 mt-4">
        {/* Amount in words */}
        <div className="flex-1">
          <p className="text-[13px] text-slate-500 font-medium mb-1">Amount in Words:</p>
          <p className="text-[15px] text-slate-800 font-bold capitalize">{numberToWords(grandTotal)}</p>
        </div>

        {/* Totals Table */}
        <div className="w-80">
          <div className="border-b border-slate-900 pb-2 mb-2 space-y-2.5">
            <div className="flex justify-between items-center text-[15px] text-slate-600">
              <span>Subtotal:</span>
              <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between items-center text-[15px] text-slate-600">
                <span>Discount:</span>
                <span className="text-[#C91D25] font-medium">- ₹{discountAmount.toLocaleString('en-IN')}</span>
              </div>
            )}
            {gstAmount > 0 && (
              <div className="flex justify-between items-center text-[15px] text-slate-600">
                <span>GST({gstValue}%):</span>
                <span className="text-slate-800 font-medium">+ ₹{gstAmount.toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center text-[16px]">
            <span className="text-slate-600">Total Amount:</span>
            <span className="font-medium text-slate-900">₹{grandTotal.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>

      {/* Change History Box */}
      <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-100">
        <div className="flex justify-between items-center mb-1">
          <p className="text-[13px] text-slate-500 font-medium">Change history</p>
          <span className="text-[11px] text-slate-400 font-medium">{logs.length > 0 ? `${logs.length} Events Recorded` : 'No events recorded'}</span>
        </div>
        <p className="text-sm text-slate-800 font-medium">
          {logs.length > 0 ? logs[logs.length - 1].action + ' - ' + (logs[logs.length - 1].notes || '') : 'No recent updates'}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3">
        <button 
          onClick={() => setShowChangeLogModal(true)}
          className="px-5 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 shadow-sm transition-all cursor-pointer"
        >
          View change log
        </button>

        {activeIndex <= 1 && !hasPaymentLog && (
          <button 
            onClick={() => setShowPaymentModal(true)}
            className="px-6 py-2.5 bg-[#0f172a] text-white rounded-lg text-sm font-semibold hover:bg-black shadow-sm transition-all flex items-center gap-2 cursor-pointer active:scale-95"
          >
            Record Advance Payment <ArrowUpRight size={16} />
          </button>
        )}
        
        {activeIndex === 2 && (
          <button disabled className="px-6 py-2.5 bg-[#0f172a] text-white rounded-lg text-sm font-semibold opacity-50 cursor-not-allowed">
            Pending Approval
          </button>
        )}
        
        {activeIndex >= 3 && (
          <button disabled className="px-6 py-2.5 bg-[#0f172a] text-white rounded-lg text-sm font-semibold opacity-50 cursor-not-allowed flex items-center gap-2">
            Already Sent to Store <ArrowUpRight size={16} />
          </button>
        )}
      </div>

      {/* PAYMENT MODAL DIALOG */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-2xl w-full p-6 relative">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-5">
              <div>
                <h3 className="text-lg font-bold text-slate-900 tracking-tight">Record Advance Payment</h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5">Order: {orderIdStr} • {customerName}</p>
              </div>
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center text-sm font-bold transition-all"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleConfirmPayment} className="space-y-5">
              {/* Payable Amount Input */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1">Payable Amount (₹) (₹{grandTotal.toLocaleString('en-IN')})</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400 font-semibold text-sm">₹</span>
                  <input
                    type="number"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    className="w-full h-10 pl-8 pr-3 rounded-lg border border-slate-300 text-slate-900 text-sm font-bold outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
              </div>

              {/* Payment Method Selector Tabs */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-2">Select Payment Method</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('cheque')}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'cheque' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    🧾 Cheque
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('upi')}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'upi' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    📱 UPI
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('cash')}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'cash' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    💵 Cash
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('bank')}
                    className={`py-2 px-2.5 rounded-lg text-xs font-bold border transition-all text-center ${paymentMethod === 'bank' ? 'bg-[#0f172a] text-white border-[#0f172a] shadow-xs' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    🏦 Bank
                  </button>
                </div>
              </div>

              {/* Dynamic Payment Method Fields */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 space-y-3">
                {paymentMethod === 'cheque' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Cheque Number <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={chequeNumber}
                        onChange={(e) => setChequeNumber(e.target.value)}
                        placeholder="6-digit No."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Cheque Date <span className="text-red-500">*</span></label>
                      <input
                        type="date"
                        value={chequeDate}
                        onChange={(e) => setChequeDate(e.target.value)}
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Issuing Bank <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={issuingBank}
                        onChange={(e) => setIssuingBank(e.target.value)}
                        placeholder="Bank name"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                  </div>
                )}

                {paymentMethod === 'upi' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">UPI UTR / Ref No. <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={upiRefNo}
                        onChange={(e) => setUpiRefNo(e.target.value)}
                        placeholder="TRX-123..."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">UPI Phone Number <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        pattern="[0-9]{10}"
                        maxLength={10}
                        value={upiPhone}
                        onChange={(e) => setUpiPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit number"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                      <input
                        type="text"
                        value={upiRemarks}
                        onChange={(e) => setUpiRemarks(e.target.value)}
                        placeholder="Internal Remarks"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                      />
                    </div>
                  </div>
                )}

                {paymentMethod === 'cash' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Receiver Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={cashReceiverName}
                        onChange={(e) => setCashReceiverName(e.target.value)}
                        placeholder="Who received"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Receiver Phone <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        pattern="[0-9]{10}"
                        maxLength={10}
                        value={cashReceiverPhone}
                        onChange={(e) => setCashReceiverPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="10-digit number"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                      <input
                        type="text"
                        value={cashInternalRemarks}
                        onChange={(e) => setCashInternalRemarks(e.target.value)}
                        placeholder="Internal Remarks"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                      />
                    </div>
                  </div>
                )}

                {paymentMethod === 'bank' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Transaction / Ref ID</label>
                      <input
                        type="text"
                        value={bankRefId}
                        onChange={(e) => setBankRefId(e.target.value)}
                        placeholder="TRX-123..."
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Bank Name</label>
                      <input
                        list="bank-names"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        placeholder="Select or enter bank name"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500 cursor-pointer"
                        required
                      />
                      <datalist id="bank-names">
                        {BANK_NAMES.map((bank) => (
                          <option key={bank} value={bank} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Branch</label>
                      <input
                        type="text"
                        value={bankBranch}
                        onChange={(e) => setBankBranch(e.target.value)}
                        placeholder="Branch Name"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">IFSC Code</label>
                      <input
                        type="text"
                        value={bankIfsc}
                        onChange={(e) => setBankIfsc(e.target.value)}
                        placeholder="SBIN0001234"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                        required
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">Internal Remarks</label>
                      <input
                        type="text"
                        value={internalRemarks}
                        onChange={(e) => setInternalRemarks(e.target.value)}
                        placeholder="Internal Remarks"
                        className="w-full h-9 px-3 rounded-md border border-slate-300 text-xs text-slate-800 bg-white outline-none focus:border-slate-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-200 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={recordPaymentMutation.isPending}
                    className="px-5 py-2 bg-[#0f172a] text-white text-xs font-bold rounded-lg hover:bg-black active:scale-95 shadow-sm transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    {recordPaymentMutation.isPending ? 'Processing...' : 'Record & Request'}
                  </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Audit History Change Log Modal */}
      {showChangeLogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 max-w-lg w-full p-6 relative max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Transaction Audit History</h3>
                <p className="text-xs text-slate-500 font-medium">Full transaction log for {orderIdStr}</p>
              </div>
              <button 
                onClick={() => setShowChangeLogModal(false)}
                className="w-8 h-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center text-sm font-bold transition-all"
              >
                ✕
              </button>
            </div>

            {/* Audit Log Timeline */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-1">
              {logs.length === 0 ? (
                <div className="text-center text-slate-500 py-10">
                  <p className="text-sm font-medium">No audit logs found for this order.</p>
                </div>
              ) : (
                <div className="space-y-4 my-2 relative before:absolute before:left-3.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-slate-200">
                  {logs.map((log, index) => (
                    <div key={log._id || index} className="flex gap-4 relative z-10">
                      <div className={`w-7 h-7 rounded-full text-white flex items-center justify-center text-xs font-bold shrink-0 ring-4 ring-white ${index === logs.length - 1 ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                        {index === logs.length - 1 ? '⏳' : '✓'}
                      </div>
                      <div>
                        <div className="text-[13px] font-bold text-slate-900">{index + 1}. {log.action}</div>
                        <div className="text-[11px] text-slate-500 font-medium mt-0.5">{log.notes || 'Status updated'}</div>
                        <div className="text-[10px] text-slate-400 font-medium mt-1">
                          {new Date(log.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                          {log.created_by && ` • ${log.created_by.name || 'System'}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end shrink-0">
              <button 
                onClick={() => setShowChangeLogModal(false)}
                className="px-5 py-2 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-black transition-all cursor-pointer"
              >
                Close Audit Log
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};
